'use client';
/* Release: 63880259 (Latest – unreleased) */

import * as Sentry from '@sentry/react';
import Page from 'components/Page';
import { ProductPlanDemo } from 'components/ProductPlanDemo';
import { RequestSubscriptionPlanExtensionCTA } from 'components/RequestSubscriptionPlanExtensionCTA';
import useUserAccountState from 'hooks/useUserAccountState';
import { useListSubscriptions } from 'src/api/frontend';
import { Alert, Breadcrumbs, Button } from 'subframe/index';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import {
  ExpiryDisplay,
  canRequestPlanExtension,
  getAlertBannerExpiryInDaysText,
  getMostRelevanSubscription,
  showUpdateNow,
} from 'utils/subscription_plan';
import { CurrentSubscription } from './components/CurrentSubscription';

function ProductPlansSubscriptions() {
  const { logEvent } = AnalyticsEventLogger();

  const { account, currentOrganization } = useUserAccountState();
  const { data, error: subscriptionError } = useListSubscriptions(
    currentOrganization.slug,
    {},
    {
      request: { headers: { authorization: 'Bearer ' + account?.token } },
    },
  );
  const mainSubscription = getMostRelevanSubscription(data?.subscriptions);
  const isDemo = mainSubscription?.plan === 'Demo' && !subscriptionError;
  const alertBannerText = {
    headerText: getAlertBannerExpiryInDaysText(
      mainSubscription?.expiry,
      mainSubscription?.name,
    ),
    descriptionTextUpgradeNow: `Upgrade Now`,
    descriptionTextRequestExtension: `Request Extension`,
  };

  if (subscriptionError) Sentry.captureException(subscriptionError);

  // @ts-ignore
  return (
    <Page title="Billing & Subscriptions">
      <div className="h-full flex-col gap-[24px] shadow-shadow pl-[12px] pr-[12px] py-[32px] items-start flex bg-default-background ml-8">
        <div className="w-full flex-col gap-[24px] items-start flex">
          <Breadcrumbs>
            <Breadcrumbs.Item>Configure</Breadcrumbs.Item>
            <Breadcrumbs.Divider />
            <Breadcrumbs.Item active={true}>
              Billing & Subscriptions
            </Breadcrumbs.Item>
          </Breadcrumbs>
          <div className="flex w-full items-end justify-between max-w-[1400px]">
            <div className="w-full flex-col items-start flex">
              <span className="text-section-header font-section-header text-default-font">
                Billing & Subscriptions
              </span>
              <span className="w-full text-body font-body text-subtext-color">
                Manage your subscription & see available plans
              </span>
            </div>
            <a
              href="https://www.chkk.io/pricing"
              target="_blank"
              rel=",noopener noreferrer"
            >
              <Button disabled={false} size="large">
                View Product Plans
              </Button>
            </a>
          </div>
          {data?.subscriptions && (
            <div className="contents">
              {showUpdateNow(mainSubscription?.plan) && (
                <Alert
                  className="max-w-[1400px]"
                  title={alertBannerText.headerText}
                  variant={
                    ExpiryDisplay(mainSubscription?.expiry) !== ''
                      ? 'error'
                      : 'neutral'
                  }
                  description={
                    <>
                      {isDemo ? (
                        <ProductPlanDemo view={'Subscription Plan'} />
                      ) : (
                        <>
                          {mainSubscription &&
                            canRequestPlanExtension(mainSubscription) && (
                              <>
                                <RequestSubscriptionPlanExtensionCTA
                                  clickHandler={() => {
                                    logEvent(
                                      'product-plans-alert-banner-click',
                                      {
                                        text: alertBannerText.descriptionTextRequestExtension,
                                      },
                                    );
                                  }}
                                  CTAType={'link'}
                                  description={
                                    alertBannerText.descriptionTextRequestExtension
                                  }
                                  subscription={mainSubscription}
                                />
                              </>
                            )}
                        </>
                      )}
                    </>
                  }
                />
              )}
            </div>
          )}
          <CurrentSubscription
            currentSubscription={mainSubscription}
            subscriptions={data?.subscriptions}
            totalEntitlement={data?.total_entitlement}
          />
        </div>
      </div>
    </Page>
  );
}

export default ProductPlansSubscriptions;
